import { matchRoutes, type RouteObject } from 'react-router-dom'

/**
 * The matched route TEMPLATE for a pathname, for `app.route` (§5).
 *
 * ── Why this exists ─────────────────────────────────────────────────────────────────────────────
 *
 * `app.path` is the real pathname, and in the portal almost every path contains customer-authored
 * segments: `/org/acme/modules/vpc/aws/1.4.2`, `/org/acme/state/prod-eu`. That makes it unbounded,
 * so "which pages does anyone actually use" is not answerable by grouping on it - every module
 * detail view is its own distinct value. `app.route` is the groupable twin
 * (`/org/:orgSlug/modules/:name/:provider/:version?`), and it is what `http.route` already is for
 * the Worker's server spans.
 *
 * ── Why it is derived from the router config, not parsed ────────────────────────────────────────
 *
 * The obvious alternative - a hand-written list of patterns in this file, or a regex that rewrites
 * uuid-ish segments to `:id` - drifts the moment someone adds a route, and drifts silently: the
 * page view keeps being emitted, just filed under the wrong template or none. So `main.tsx` hands
 * its own route array to `registerRoutes` and this asks react-router's own `matchRoutes` to rank
 * and match it. A new route is therefore instrumented by existing, and the ranking (static beats
 * dynamic beats splat) is the router's, not a reimplementation of it.
 *
 * Registration happens at module scope in `main.tsx`, before the first render and so before the
 * first `trackPageView`. If it somehow has not, this returns undefined and the page view is emitted
 * without `app.route` - a missing attribute, never a wrong one, and never a throw on a navigation.
 */
let registered: RouteObject[] = []

export function registerRoutes(routes: RouteObject[]): void {
  registered = routes
}

/**
 * Join one `matchRoutes` result into a single template.
 *
 * Exported for the test, and separate from `routeTemplate` so the joining rules are testable
 * against a synthetic route tree with no router or DOM involved.
 *
 * Three cases make the filtering necessary, all of them present in the real config:
 *  - a pathless layout route (`AuthGuard`, which has `element` but no `path`) contributes nothing;
 *  - an index route (`{ index: true }`) likewise has no path of its own;
 *  - a child path is relative, so the segments are joined, while a leading `/` on any of them (the
 *    top-level `/callback`) must not produce `//`.
 */
export function templateFor(routes: RouteObject[], pathname: string): string | undefined {
  const matches = matchRoutes(routes, pathname)
  if (!matches || matches.length === 0) return undefined
  const segments = matches
    .map((m) => m.route.path?.replace(/^\/+|\/+$/g, '') ?? '')
    .filter((part) => part.length > 0)
  return `/${segments.join('/')}`
}

/** The template for a pathname under the registered config, or undefined if nothing matched. */
export function routeTemplate(pathname: string): string | undefined {
  try {
    if (registered.length === 0) return undefined
    return templateFor(registered, pathname)
  } catch {
    // matchRoutes throws on a malformed pattern. A navigation must still be tracked.
    return undefined
  }
}
